import React, { ReactNode, useState } from 'react';
import { Header } from '@mds/ui';
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    CircularProgress,
    Link,
    Stack,
    Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { AlertMessage } from '../../shared/types';
import ManageTemplateDialog from './ManageTemplateDialog';

export enum ManageTemplateInstance {
    Category = 'category',
    PromotionalContent = 'promotional content',
    PromotionPage = 'promotion page',
    Subscription = 'subscribe',
    Tag = 'tag',
    User = 'user',
    Article = 'article',
    Desk = 'desk',
    Template = 'template',
    Channel = 'Channel',
    ChannelGroup = 'channel group',
    UserGuideScope = 'user guide scope',
    Topic = 'Topic',
    ChannelSubscriber = 'subscriber',
    ChannelSubscribers = 'subscribers',
}

export interface WithManageTemplateProps
    extends ManageTemplateHeaderProps, ManageTemplateDialogBase {
    children: ReactNode;
    formMessage: AlertMessage;
    formBusy: boolean;
    onCancel: () => void;
    onSubmit: (ev: any) => void | Promise<void>;
    showSubmitButton?: boolean;
    showCancelButton?: boolean;
    onAlertBoxClose: () => void;
    helperContent?: JSX.Element;
    outOfFormContent?: JSX.Element;
    templateCreateButtonLabel?: string;
    templateUpdateButtonLabel?: string;
    templateDeleteButtonLabel?: string;
    maxContentWidth?: string | number;
    contentWidth?: string | number;
    templateDeleteDialogTitle?: string;
    isReadOnly?: boolean;
    rightAction?: ReactNode;
}

const ManageTemplate = (props: WithManageTemplateProps): JSX.Element => {
    const {
        isReadOnly = false,
        children,
        isNew,
        templateInstance,
        pathArea,
        returnUrl,
        formMessage,
        onAlertBoxClose,
        onSubmit,
        onCancel,
        dialogMessage,
        helperContent,
        onDelete = () => {},
        showDelete = true,
        deleteDialogBtnDisabled = false,
        outOfFormContent,
        templateCreateButtonLabel = `Add new ${templateInstance}`,
        templateUpdateButtonLabel = 'Update',
        templateDeleteButtonLabel = `Delete ${templateInstance}`,
        breadcrumbOverride,
        maxContentWidth,
        contentWidth,
        templateDeleteDialogTitle = 'Delete',
        showSubmitButton = true,
        showCancelButton = true,
        titleOverride,
        rightAction,
    } = props;
    const { openDialog, dialogOpenState, closeDialog } = useDialog();

    return (
        <Box sx={{ maxWidth: maxContentWidth }}>
            <ManageTemplateHeader
                isNew={isNew}
                templateInstance={templateInstance}
                pathArea={pathArea}
                returnUrl={returnUrl}
                breadcrumbOverride={breadcrumbOverride}
                titleOverride={titleOverride}
            />
            {formMessage?.message && (
                <Alert
                    id="manage-template-alert"
                    severity={getAlertSeverity(formMessage.style)}
                    onClose={onAlertBoxClose}
                    sx={{ my: 2 }}
                >
                    {formMessage.message}
                </Alert>
            )}
            {helperContent}
            <form autoComplete="off" onSubmit={onSubmit} onReset={onCancel}>
                <Box sx={{ width: contentWidth ?? 384, maxWidth: '100%' }}>
                    {children}
                </Box>
            </form>
            {outOfFormContent && (
                <Box sx={{ width: contentWidth ?? 384, maxWidth: '100%' }}>
                    {outOfFormContent}
                </Box>
            )}
            <Stack
                direction="row"
                spacing={2}
                sx={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 4,
                    mb: 2,
                    width: contentWidth ?? 384,
                    maxWidth: '100%',
                }}
            >
                <Stack direction="row" spacing={1}>
                    {showSubmitButton && (
                        <Button
                            id={`${templateInstance}-submit-button`}
                            variant="contained"
                            onClick={onSubmit}
                            disabled={isReadOnly || props.formBusy}
                            startIcon={
                                props.formBusy ? (
                                    <CircularProgress
                                        size={16}
                                        color="inherit"
                                    />
                                ) : undefined
                            }
                        >
                            {isNew
                                ? templateCreateButtonLabel
                                : templateUpdateButtonLabel}
                        </Button>
                    )}
                    {showCancelButton && (
                        <Button
                            id={`${templateInstance}-cancel-button`}
                            variant="outlined"
                            color="secondary"
                            onClick={onCancel}
                        >
                            Cancel
                        </Button>
                    )}
                </Stack>
                {rightAction ??
                    (!isNew && showDelete && (
                        <Button
                            id={`${templateInstance}-delete-button`}
                            color="error"
                            variant="outlined"
                            onClick={(): void => {
                                openDialog();
                            }}
                        >
                            {templateDeleteButtonLabel}
                        </Button>
                    ))}
            </Stack>
            {!isReadOnly && showDelete && (
                <ManageTemplateDialog
                    showDelete={showDelete}
                    submitBtnDisabled={deleteDialogBtnDisabled}
                    onDelete={onDelete}
                    submitBtnColorType="error"
                    dialogMessage={dialogMessage}
                    title={`${templateDeleteDialogTitle} this ${templateInstance}?`}
                    submitBtnText={templateDeleteButtonLabel}
                    dialogOpenState={dialogOpenState}
                    openDialog={openDialog}
                    closeDialog={closeDialog}
                />
            )}
        </Box>
    );
};

interface ManageTemplateHeaderProps {
    isNew: boolean;
    templateInstance: ManageTemplateInstance;
    breadcrumbOverride?: JSX.Element;
    pathArea: string;
    returnUrl: string;
    titleOverride?: string;
}

export const ManageTemplateHeader = (
    props: ManageTemplateHeaderProps
): JSX.Element => {
    const {
        isNew,
        templateInstance,
        pathArea,
        returnUrl,
        breadcrumbOverride,
        titleOverride,
    } = props;
    const title = isNew
        ? `Add ${templateInstance === ManageTemplateInstance.PromotionalContent || templateInstance === ManageTemplateInstance.ChannelSubscribers ? '' : 'a '}new ${templateInstance}`
        : `Edit ${templateInstance === ManageTemplateInstance.PromotionalContent ? '' : 'a '}${templateInstance}`;
    const pathOperation = isNew
        ? `New ${templateInstance}`
        : `Edit ${templateInstance}`;

    return (
        <Box sx={{ mt: 2, mb: 2 }}>
            <Header
                title={titleOverride || title}
                breadcrumbs={
                    breadcrumbOverride || (
                        <Breadcrumbs aria-label={`${pathArea} breadcrumb`}>
                            <Link component={RouterLink} to={returnUrl}>
                                {pathArea}
                            </Link>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: 'text.primary',
                                }}
                            >
                                {pathOperation}
                            </Typography>
                        </Breadcrumbs>
                    )
                }
            />
        </Box>
    );
};

export interface ManageTemplateDialogBase {
    dialogMessage?: ReactNode;
    onDelete?: () => void;
    showDelete?: boolean;
    deleteDialogBtnDisabled?: boolean;
}

export interface ManageTemplateDialogState {
    dialogOpenState: boolean;
    openDialog: () => void;
    closeDialog: () => void;
}

const useDialog = (): ManageTemplateDialogState => {
    const [dialogOpenState, setDialogOpenState] = useState<boolean>(false);

    const openDialog = (): void => setDialogOpenState(true);
    const closeDialog = (): void => setDialogOpenState(false);

    return { dialogOpenState, openDialog, closeDialog };
};

const getAlertSeverity = (style?: AlertMessage['style']) =>
    style === 'grey' || !style ? 'info' : style;

export default ManageTemplate;
